import React from "react";
import { Link } from "react-router-dom";
import { Check, ArrowRight, Trophy } from "lucide-react";
import { COMPETITORS } from "../data/competitors";
import { BRAND } from "../lib/brand";
import Faq from "../components/Faq";
import Reveal from "../components/Reveal";
import { useSeo, faqLd, breadcrumbLd, softwareLd } from "../lib/seo";

const VARIANTS = {
  best: {
    path: "/best-ai-face-analysis-tool",
    h1: "The 12 Best AI Face Analysis Tools in 2026",
    title: "Best AI Face Analysis Tools (2026 Ranked & Compared)",
    intro: "We ranked every major AI face-analysis and beauty-scoring tool on platform, price, depth and features. Here's the honest 2026 list — with the best web-based option at the top.",
    keyword: "AI face analysis tool",
  },
  looksmax: {
    path: "/best-looksmaxxing-app",
    h1: "The Best Looksmaxxing Apps in 2026",
    title: "Best Looksmaxxing Apps (2026 Honest Ranking)",
    intro: "Looking for the best looksmaxxing app? We compared the most popular score-and-improve apps on price, platform and how actionable they really are.",
    keyword: "looksmaxxing app",
  },
};

export default function CategoryPage({ variant = "best" }) {
  const v = VARIANTS[variant];
  const faqs = [
    { q: `What is the best ${v.keyword}?`, a: `For most people the best option is a web-based tool that's free to start, gives a detailed breakdown and a real improvement plan. ${BRAND.name} leads on all three, and works on any device with no download.` },
    { q: `Are these tools accurate?`, a: `They estimate facial geometry from a photo. Accuracy depends on photo quality and consistency. Tools that report multiple metrics (like ${BRAND.name}'s 8) make tracking progress clearer than a single opaque score.` },
    { q: `Is there a free ${v.keyword}?`, a: `Yes — ${BRAND.name} offers a full free analysis with a shareable result. Many competitors require a weekly subscription before showing results.` },
  ];

  useSeo({
    title: v.title,
    description: v.intro,
    path: v.path,
    jsonLd: [softwareLd, faqLd(faqs), breadcrumbLd([{ name: "Home", path: "/" }, { name: v.h1, path: v.path }])],
  });

  const ranked = [
    { name: BRAND.name, platform: "Web (any device)", price: "Free / $12 mo", note: "Best overall — 8 metrics, AI Coach, projection, sharing & leaderboard. No download.", us: true },
    ...COMPETITORS.slice(0, 9).map((c) => ({ name: c.name, platform: c.platform, price: c.price, note: c.tagline, slug: c.slug })),
  ];

  return (
    <div className="container py-12">
      <Reveal>
        <span className="label-eyebrow">Ranked & compared</span>
        <h1 className="mt-3 font-display text-5xl text-balance sm:text-6xl">{v.h1}</h1>
        <p className="mt-4 max-w-2xl text-muted-foreground">{v.intro}</p>
        <Link to="/" className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition hover:brightness-110" data-testid="category-cta">
          Try the #1 pick free <ArrowRight className="h-4 w-4" />
        </Link>
      </Reveal>

      <div className="mt-12 space-y-4">
        {ranked.map((r, i) => (
          <Reveal key={r.name} delay={i * 0.03}>
            <div className={`flex flex-col gap-4 rounded-xl border bg-card p-6 sm:flex-row sm:items-center ${r.us ? "border-primary" : "border-border"}`} data-testid={`rank-${i + 1}`}>
              <div className="flex items-center gap-4 sm:w-64">
                <span className="font-display text-3xl text-primary">#{i + 1}</span>
                <div>
                  <p className="flex items-center gap-2 font-display text-xl">{r.name} {r.us && <Trophy className="h-4 w-4 text-primary" />}</p>
                  <p className="text-xs text-muted-foreground">{r.platform}</p>
                </div>
              </div>
              <p className="flex-1 text-sm text-muted-foreground">{r.note}</p>
              <div className="flex items-center gap-4">
                <span className="text-sm font-mono">{r.price}</span>
                {r.us ? (
                  <Link to="/" className="rounded-md bg-primary px-4 py-2 text-xs font-semibold text-primary-foreground">Try free</Link>
                ) : r.slug ? (
                  <Link to={`/vs/${r.slug}`} className="rounded-md border border-border px-4 py-2 text-xs transition hover:border-primary/50">Compare</Link>
                ) : null}
              </div>
            </div>
          </Reveal>
        ))}
      </div>

      <div className="mt-12 rounded-xl border border-primary/30 bg-card p-6">
        <h2 className="font-display text-2xl">Why {BRAND.name} ranks #1</h2>
        <ul className="mt-4 grid gap-3 sm:grid-cols-2">
          {["Works in any browser — no iPhone or download", "Full result free, no weekly trial trap", "8 detailed metrics + per-metric notes", "AI Coach plan with timeframes", "Future-Face projection", "Public share pages + global leaderboard"].map((f) => (
            <li key={f} className="flex items-start gap-2 text-sm"><Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />{f}</li>
          ))}
        </ul>
      </div>

      <section className="mt-14 max-w-3xl">
        <h2 className="font-display text-3xl">FAQ</h2>
        <div className="mt-6"><Faq faqs={faqs} testId="category-faq" /></div>
      </section>
    </div>
  );
}
